import { RECENCY_DAYS } from "./vocabulary";
/**
 * The measures every figure is built from (ADR 0016; docs/admin-app/figures.md › Measures). The
 * data has few garages, heavily skewed counts and many zeros, so the figures lean on the median,
 * percentiles, shares and top lists rather than on the mean and the min. An installation and core
 * compute a figure with the same function, never a second way.
 */

/**
 * Lower edges of the histogram buckets: 0 · 1 · 2–5 · 6–10 · 11–25 · 26–50 · 51–100 · 101–250 ·
 * 251–500 · 501–1,000 · over 1,000. They never change, so histograms from different months compare.
 */
export const HISTOGRAM_EDGES = [0, 1, 2, 6, 11, 26, 51, 101, 251, 501, 1001] as const;

/** Under this base a figure is shown as "3 of 7", without a percentage. */
export const SMALL_BASE = 20;

/** Under this many values nothing is flagged as an outlier. */
export const OUTLIER_MIN_VALUES = 8;

/** How far above the third quartile, in interquartile ranges on log(1 + x), an outlier lies. */
export const OUTLIER_IQR_FACTOR = 3;

/**
 * A distribution over garages (or users): counts first, then the shape. A type rather than an
 * interface, so it fits the report's objects, which keep unknown fields.
 */
export type Distribution = {
  /** How many values it is over. */
  n: number;
  /** How many of them are 0: the share with none replaces the min, which is almost always 0. */
  none: number;
  p25: number;
  median: number;
  p75: number;
  p90: number;
  max: number;
  /** Shown next to the median and labelled: far above it means a few big garages. */
  mean: number;
  /** Counts per bucket of `HISTOGRAM_EDGES`. */
  histogram: number[];
};

/** The values sorted ascending, for `percentile`. */
function sorted(values: readonly number[]): number[] {
  return [...values].sort((a, b) => a - b);
}

/** Percentile `p` (0–1) of ascending values, interpolated linearly (type 7, as R and NumPy do). */
export function percentile(ascending: readonly number[], p: number): number {
  if (ascending.length === 0) return 0;
  const h = (ascending.length - 1) * p;
  const lo = Math.floor(h);
  const low = ascending[lo] ?? 0;
  const high = ascending[Math.min(lo + 1, ascending.length - 1)] ?? low;
  return low + (h - lo) * (high - low);
}

/** The index of the bucket `value` falls in, for buckets given by their ascending lower edges. */
export function bucketIndex(value: number, lowerEdges: readonly number[]): number {
  let index = 0;
  for (let i = 0; i < lowerEdges.length; i++) {
    if (value >= (lowerEdges[i] ?? 0)) index = i;
    else break;
  }
  return index;
}

/** How many values fall in each bucket of `lowerEdges`. */
export function histogram(values: readonly number[], lowerEdges: readonly number[]): number[] {
  const counts = lowerEdges.map(() => 0);
  for (const value of values) {
    const index = bucketIndex(value, lowerEdges);
    counts[index] = (counts[index] ?? 0) + 1;
  }
  return counts;
}

/**
 * The distribution of `values`, e.g. parts per garage. With `aboveZero` it is over the values above
 * zero only: the intensity among the garages that use a feature, next to its adoption.
 */
export function distribution(
  values: readonly number[],
  { aboveZero = false }: { aboveZero?: boolean } = {},
): Distribution {
  const ascending = sorted(aboveZero ? values.filter((v) => v > 0) : values);
  const n = ascending.length;
  return {
    n,
    none: ascending.filter((v) => v === 0).length,
    p25: percentile(ascending, 0.25),
    median: percentile(ascending, 0.5),
    p75: percentile(ascending, 0.75),
    p90: percentile(ascending, 0.9),
    max: ascending[n - 1] ?? 0,
    mean: n ? ascending.reduce((sum, v) => sum + v, 0) / n : 0,
    histogram: histogram(ascending, HISTOGRAM_EDGES),
  };
}

/** A part of a base, with a percentage only from `SMALL_BASE` up. */
export interface Share {
  part: number;
  base: number;
  /** 0–100, or null under the small base: then the figure reads "3 of 7". */
  percent: number | null;
}

export function share(part: number, base: number): Share {
  return { part, base, percent: base >= SMALL_BASE ? (part / base) * 100 : null };
}

/** The change between a series' first and last value. */
export interface Growth {
  from: number;
  to: number;
  /** Always shown, next to the percentage if there is one. */
  change: number;
  /** 0–100 and more, or null when the base is under `SMALL_BASE`. */
  percent: number | null;
}

/** Growth over a series, e.g. month over month or year over year: its first value against its last. */
export function growth(series: readonly number[]): Growth {
  const from = series[0] ?? 0;
  const to = series[series.length - 1] ?? 0;
  return {
    from,
    to,
    change: to - from,
    percent: from >= SMALL_BASE ? ((to - from) / from) * 100 : null,
  };
}

/**
 * Which values are outliers: above Q3 + 3 × IQR on log(1 + x), which suits counts skewed by a few
 * big garages. Nothing is flagged under `OUTLIER_MIN_VALUES` values.
 */
export function outliers(values: readonly number[]): boolean[] {
  if (values.length < OUTLIER_MIN_VALUES) return values.map(() => false);
  const logs = values.map((v) => Math.log1p(Math.max(0, v)));
  const ascending = sorted(logs);
  const q1 = percentile(ascending, 0.25);
  const q3 = percentile(ascending, 0.75);
  const limit = q3 + OUTLIER_IQR_FACTOR * (q3 - q1);
  return logs.map((v) => v > limit);
}

/**
 * Percentile `p` (0–1) estimated from bucket counts, e.g. a latency p95: linear within the bucket
 * that holds it. In the last, open bucket it is that bucket's lower edge ("over 10 s").
 */
export function bucketPercentile(
  counts: readonly number[],
  lowerEdges: readonly number[],
  p: number,
): number {
  const total = counts.reduce((sum, c) => sum + c, 0);
  if (total === 0) return 0;
  const rank = p * total;
  let below = 0;
  for (let i = 0; i < counts.length; i++) {
    const count = counts[i] ?? 0;
    const low = lowerEdges[i] ?? 0;
    const high = lowerEdges[i + 1];
    if (count > 0 && below + count >= rank) {
      if (high === undefined) return low;
      return low + ((rank - below) / count) * (high - low);
    }
    below += count;
  }
  return lowerEdges[lowerEdges.length - 1] ?? 0;
}

/**
 * Garages added in a month, and how many of them were active in each month since. A type, so it
 * fits the report's objects, which keep unknown fields.
 */
export type Cohort = {
  /** "YYYY-MM". */
  month: string;
  added: number;
  /** `active[i]`: how many were used in the i-th month after `month` (0: that month itself). */
  active: number[];
};

/** The month after "YYYY-MM". */
function nextMonth(month: string): string {
  const [year = 0, m = 1] = month.split("-").map(Number);
  return m === 12 ? `${year + 1}-01` : `${year}-${String(m + 1).padStart(2, "0")}`;
}

/**
 * Cohorts of garages by the month they were added, up to the month of `today` (UTC). Every month
 * from the first garage's is a row, also one in which none was added, so the table has no holes.
 */
export function cohorts(
  garages: readonly { id: string; createdOn: string }[],
  activeDays: readonly { garageId: string; day: string }[],
  today: string,
): Cohort[] {
  const last = today.slice(0, 7);
  const first = garages.reduce<string | null>((min, g) => {
    const month = g.createdOn.slice(0, 7);
    return min === null || month < min ? month : min;
  }, null);
  if (first === null || first > last) return [];
  const months: string[] = [];
  for (let month = first; month <= last; month = nextMonth(month)) months.push(month);
  const activeIn = new Map<string, Set<string>>();
  for (const { garageId, day } of activeDays) {
    const month = day.slice(0, 7);
    const ids = activeIn.get(month) ?? new Set<string>();
    ids.add(garageId);
    activeIn.set(month, ids);
  }
  return months.map((month, index) => {
    const cohort = garages.filter((g) => g.createdOn.slice(0, 7) === month);
    return {
      month,
      added: cohort.length,
      active: months
        .slice(index)
        .map((m) => cohort.filter((g) => activeIn.get(m)?.has(g.id)).length),
    };
  });
}

/**
 * Stickiness: the mean daily active users over the monthly active ones, as a share, so a base
 * under `SMALL_BASE` (including none) gives no percentage.
 */
export function stickiness(meanDau: number, mau: number): Share {
  return share(meanDau, mau);
}

/**
 * The trailing mean over `window` points of a daily series; null where fewer points lie behind,
 * so a chart doesn't show a mean of fewer days as if it were a full one.
 */
export function rollingMean(series: readonly number[], window = 7): (number | null)[] {
  let sum = 0;
  return series.map((value, i) => {
    sum += value - (i >= window ? (series[i - window] ?? 0) : 0);
    return i >= window - 1 ? sum / window : null;
  });
}

/** A distribution per garage both ways: over every garage, and over those with at least one. */
export function perGarage(values: readonly number[]): { all: Distribution; using: Distribution } {
  return { all: distribution(values), using: distribution(values, { aboveZero: true }) };
}

/**
 * The leading hex digits of a UUIDv7 id an installation groups by: its top 28 bits of Unix
 * milliseconds, buckets of 2^20 ms (about 17 minutes). Added per day goes by the id rather than
 * `created_at`, because an imported garage package keeps its records' old `created_at`.
 */
export const ID_TIME_PREFIX_LENGTH = 7;
const ID_PREFIX_MS = 2 ** 20;

/** The prefix of the ids made at `ms`: ids from then on compare as text at least as great. */
export function idPrefixAt(ms: number): string {
  return Math.floor(ms / ID_PREFIX_MS)
    .toString(16)
    .padStart(ID_TIME_PREFIX_LENGTH, "0");
}

/** The UTC day a bucket of `ID_TIME_PREFIX_LENGTH` hex digits starts on, or null for another id. */
export function dayOfIdPrefix(prefix: string): string | null {
  if (!new RegExp(`^[0-9a-f]{${ID_TIME_PREFIX_LENGTH}}$`, "i").test(prefix)) return null;
  return new Date(Number.parseInt(prefix, 16) * ID_PREFIX_MS).toISOString().slice(0, 10);
}

/** Counts per id prefix as counts per UTC day, oldest first; ids that aren't UUIDv7 are skipped. */
export function perDayFromIdPrefixes(
  rows: readonly { prefix: string; count: number }[],
): { day: string; count: number }[] {
  const days = new Map<string, number>();
  for (const { prefix, count } of rows) {
    const day = dayOfIdPrefix(prefix);
    if (day) days.set(day, (days.get(day) ?? 0) + count);
  }
  return [...days].sort(([a], [b]) => (a < b ? -1 : 1)).map(([day, count]) => ({ day, count }));
}

/**
 * The recency bucket of something last seen `daysAgo` days ago: within 7, 30, 90, 180 or 365 days
 * (`RECENCY_DAYS`), or over a year (the last bucket).
 */
export function recencyBucket(daysAgo: number): number {
  const index = RECENCY_DAYS.findIndex((days) => daysAgo <= days);
  return index === -1 ? RECENCY_DAYS.length : index;
}
